import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'
import * as React from 'react'
import { hasText } from 'utils/helpers'
import {
  dateOfBirthRequiredSchema,
  emailSchema,
  formSchema,
  fullNameSchema,
  packetSchema,
  phoneSchema,
  schemasValidate,
} from 'utils/schemas'

import { splitFullName } from '@/utils/splitFullName'

const initialFormError = {
  full_name: false,
  first_name: false,
  last_name: false,
  email: false,
  phone: false,
  form: false,
}

const initialFormErrorLabel = {
  full_name: undefined,
  first_name: undefined,
  last_name: undefined,
  email: undefined,
  phone: undefined,
  form: undefined,
}

function useSendForm({ isPacket = false, forceDob = false }) {
  const [willUseEmail, setUseEmail] = React.useState(true)
  const [email, setEmail] = React.useState('')
  const [phone, setPhone] = React.useState('')
  const [dob, setDob] = React.useState('')
  const [scheduledDate, setScheduledDate] = React.useState(null)

  const [selectedOption, setSelectedOption] = React.useState(null)

  const [id, setId] = React.useState('')
  const [personToAdd, setPersonToAdd] = React.useState({})
  const [person, setPerson] = React.useState(null)

  const [hasFormError, setHasFormError] = React.useState(false)
  const [formError, setFormError] = React.useState(initialFormError)
  const [formErrorLabel, setFormErrorLabel] = React.useState(
    initialFormErrorLabel
  )

  function onCreatePerson(input, type) {
    if (type === 'full_name') {
      const separateName = splitFullName(input)
      schemasValidate(
        [[type, separateName, fullNameSchema]],
        setFormError,
        setFormErrorLabel
      )
      setPersonToAdd((person) => ({ ...person, ...separateName }))
    } else {
      setPersonToAdd((person) => ({ ...person, [type]: input }))
    }
    setPerson(null)
  }

  // #region Form
  function onPersonSelected(data) {
    const person = data.title

    if (data?.is_created) {
      onCreatePerson(data?.label, 'full_name')
      return
    }

    setPerson(person)
    setPersonToAdd({})
    const hasEmail = hasText(person?.email)
    const hasPhone = hasText(person?.phone_number)
    setEmail(person?.email ?? '')
    setPhone(person?.phone_number ?? '')
    setDob(person?.date_birth ?? '')

    if (willUseEmail && !hasEmail && hasPhone) {
      setUseEmail(false)
    } else if (!willUseEmail && hasEmail && !hasPhone) {
      setUseEmail(true)
    }

    schemasValidate(
      [
        [
          'full_name',
          { first_name: person?.first_name, last_name: person?.last_name },
          fullNameSchema,
        ],
        ...(hasEmail && willUseEmail
          ? [['email', person?.email, emailSchema]]
          : []),
        ...(hasPhone && !willUseEmail
          ? [['phone', person?.phone_number, phoneSchema]]
          : []),
        ...(person?.date_birth && forceDob
          ? [['dob', person?.date_birth, dateOfBirthRequiredSchema]]
          : []),
      ],
      setFormError,
      setFormErrorLabel
    )
  }

  function onClearSelectedPerson() {
    setPerson(null)
    setPersonToAdd({})
    setEmail('')
    setPhone('')
    setDob('')
    setScheduledDate(null)
  }

  const onChangeEmailToggle = React.useCallback(() => {
    setUseEmail((p) => !p)
    if (emailSchema.isValidSync(email) === false) {
      setEmail('')
    }
    if (phoneSchema.isValidSync(phone) === false) {
      setPhone('')
    }
    setFormErrorLabel((p) => ({
      ...p,
      email: undefined,
      phone: undefined,
    }))
    setFormError((p) => ({
      ...p,
      email: false,
      phone: false,
    }))
  }, [email, phone])

  const validateEmail = React.useCallback(async () => {
    schemasValidate(
      [['email', email, emailSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [email])
  React.useEffect(() => {
    formError?.email === true && validateEmail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  const validatePhone = React.useCallback(() => {
    schemasValidate(
      [['phone', phone, phoneSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [phone])

  const validateDob = React.useCallback(
    (value) => {
      let dateValue = null

      if (typeof value !== 'string') return

      if (value || dob) {
        const bothValues = value || dob || ''
        const [year, month, day] = String(bothValues)?.split('-').map(Number)

        dateValue = new Date(year, month - 1, day)
      }

      schemasValidate(
        [['dob', dateValue, dateOfBirthRequiredSchema]],
        setFormError,
        setFormErrorLabel
      )
    },
    [dob]
  )

  React.useEffect(() => {
    formError?.phone === true && validatePhone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone])

  React.useEffect(() => {
    setHasFormError((p) => {
      const n =
        isObject(formError) &&
        Object.values(formError)?.some((err) => err === true)
      return n !== p ? n : p
    })
  }, [formError])

  async function onFormSelected(formId, option) {
    setId(formId)
    setSelectedOption(option)
    schemasValidate(
      [['form', formId, isPacket ? packetSchema : formSchema]],
      setFormError,
      setFormErrorLabel
    )
  }

  function validateForm() {
    const personToUse = isNil(person) ? personToAdd : person
    const validations = [
      [
        'full_name',
        {
          first_name: personToUse?.first_name,
          last_name: personToUse?.last_name,
        },
        fullNameSchema,
      ],
      ['form', id, isPacket ? packetSchema : formSchema],
    ]

    if (forceDob) {
      validations.push(['dob', dob, dateOfBirthRequiredSchema])
    }

    if (!forceDob && willUseEmail) {
      validations.push(['email', email, emailSchema])
    }

    if (!forceDob && !willUseEmail) {
      validations.push(['phone', phone, phoneSchema])
    }
    return schemasValidate(validations, setFormError, setFormErrorLabel)
  }
  // #endregion Form

  return {
    email,
    person,
    personToAdd,
    willUseEmail,
    phone,
    id,
    dob,
    setDob,
    scheduledDate,
    setScheduledDate,

    setEmail,
    validateEmail,
    setPhone,
    validatePhone,
    setPerson,

    hasFormError,
    formError,
    formErrorLabel,

    onPersonSelected,
    onClearSelectedPerson,
    onCreatePerson,
    onChangeEmailToggle,
    onFormSelected,
    validateForm,
    validateDob,

    selectedOption,
  }
}

export default useSendForm
